<script>
  /**
   * Distintivo de la entidad financiera.
   *
   * ── POR QUÉ NO SON LOS LOGOS REALES ─────────────────────────────────────────
   *
   * Los logos de los bancos son marcas registradas. Copiarlos dentro de la app
   * significaría o bien descargarlos de cada banco (que cambian sin avisar y
   * romperían la app), o bien traerlos de un servicio externo (que necesita
   * internet, y esta app tiene que funcionar sin él).
   *
   * En vez de eso: la inicial de la marca sobre su COLOR REAL. Es lo que hacen
   * varias apps de finanzas cuando no tienen los derechos, y funciona porque el
   * color es lo que uno reconoce de lejos — el morado de Nequi, el amarillo de
   * Bancolombia, el morado de Nu.
   *
   * Si algún día quieres los logos de verdad: pon los archivos PNG en
   * `public/logos/` con el nombre de la entidad en minúscula (nequi.png,
   * bancolombia.png) y este componente los usa automáticamente.
   */

  let { entidad = null, tamano = 40, icono = null } = $props();

  /**
   * Decide si el texto va blanco o negro según qué tan claro sea el fondo.
   *
   * Sin esto, el amarillo de Bancolombia con letras blancas quedaría
   * ilegible. La fórmula es la de luminancia relativa de WCAG: no es lo
   * mismo "claro a la vista" que "claro matemáticamente".
   */
  function tintaSobre(hex) {
    if (!hex) return '#FFFFFF';
    const n = parseInt(hex.replace('#', ''), 16);
    const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    const luminancia = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    // 0.36 es el punto donde el contraste con blanco y con negro se empata.
    return luminancia > 0.36 ? '#14120F' : '#FFFFFF';
  }

  /** La marca escrita: dos letras para las cortas, una para las largas. */
  function marca(nombre) {
    if (!nombre) return '?';
    const limpio = nombre.replace(/^Banco (de )?/i, '').trim();
    // Nu y Addi se leen mejor completas: son marcas de dos y cuatro letras.
    if (limpio.length <= 4) return limpio;
    const palabras = limpio.split(/\s+/);
    if (palabras.length > 1) return (palabras[0][0] + palabras[1][0]).toUpperCase();
    return limpio.slice(0, 2);
  }

  const color = $derived(entidad?.color ?? '#736C65');
  const tinta = $derived(tintaSobre(color));
  const texto = $derived(marca(entidad?.nombre));
  const archivo = $derived(
    entidad?.nombre
      ? `/logos/${entidad.nombre.toLowerCase().replace(/[^a-z0-9]/g, '')}.png`
      : null
  );

  let usarImagen = $state(true);
</script>

<span
  class="distintivo"
  style="--tam:{tamano}px; --color:{color}; --tinta:{tinta}"
  aria-hidden="true"
>
  {#if entidad && archivo && usarImagen}
    <!-- Si existe el archivo del logo real, se usa. Si no, cae al distintivo
         de color sin que se note. -->
    <img src={archivo} alt="" onerror={() => (usarImagen = false)} />
  {:else if entidad}
    <span class="marca" class:larga={texto.length > 2}>{texto}</span>
  {:else}
    <span class="marca">{icono ?? '•'}</span>
  {/if}
</span>

<style>
  .distintivo {
    width: var(--tam);
    height: var(--tam);
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    border-radius: var(--r-full);
    background: var(--color);
    color: var(--tinta);
    overflow: hidden;
    /* Un borde interior muy sutil: despega el distintivo del fondo cuando el
       color de la marca es casi blanco, sin que se vea como un borde. */
    box-shadow: inset 0 0 0 1px rgb(0 0 0 / 0.08);
  }

  .distintivo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .marca {
    font-size: calc(var(--tam) * 0.42);
    font-weight: var(--w-bold);
    letter-spacing: -0.03em;
    line-height: 1;
    font-variant-numeric: normal;
  }
  /* Las marcas de 3 o 4 letras (Addi, Nu) necesitan achicarse para caber. */
  .marca.larga { font-size: calc(var(--tam) * 0.32); letter-spacing: -0.04em }
</style>
